import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Card,
  Table,
  Space,
  Drawer,
  Button,
  Select,
  Input,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Empty,
  Spin,
  Modal,
  Descriptions,
  DatePicker
} from 'antd'
import type { SelectProps, TableColumnsType } from 'antd'
import { ReloadOutlined, FileExcelOutlined } from '@ant-design/icons'
import moment from 'moment'
import { apiService } from '../../../services/apiService'

type Entry = {
  id?: number
  orderId?: number
  skuId: string
  skuNameEn?: string
  skuNameZh?: string
  brand?: string
  quantity?: string | number
  totalPrice?: string | number
  pk?: string | null
  createdAt?: string
  updatedAt?: string
}

type Detail = {
  id?: number
  consignmentCode?: string
  trackingId?: string
  transactionId?: string
  orderDate?: string
  consignmentStatus?: string
  orderId?: string
  pickUpDate?: string
  deliveryDate?: string
  deliveryMethod?: string
  recipientName?: string | null
  contactPhoneNumber?: string | null
  deliveryAddress?: string | null
  consignmentEntries?: Entry[]
  warehouseId?: string
  deliverToWarehouse?: string
  message?: string | null
  createdAt?: string
  updatedAt?: string
}

interface APIResponse<T> {
  success: boolean
  data: T
  pagination?: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

type StoreGroup = {
  storeCode: string
  orders: Detail[]
  totalOrders: number
}

const fmt = (v?: string | null) => (v == null ? '-' : String(v))


const getStatusColor = (status: string) => {
  switch ((status || '').toUpperCase()) {
    case 'ACKNOWLEDGED':
      return 'blue'
    case 'PENDING':
      return 'orange'
    case 'SHIPPED':
      return 'cyan'
    case 'DELIVERED':
      return 'green'
    case 'CONFIRMED':
      return 'processing'
    case 'ORDER_COMPLETE':
      return 'geekblue'
    case 'CS_CANCEL':
      return 'volcano'
    default:
      return 'default'
  }
}

const BASE_STATUS_SEQUENCE = [
  'PENDING',
  'ACKNOWLEDGED',
  'CONFIRMED',
  'ORDER_COMPLETE',
  'CS_CANCEL',
  'SHIPPED',
  'DELIVERED',
] as const

const HKTVmallOrder: React.FC = () => {
  const [orders, setOrders] = useState<Detail[]>([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<Detail | null>(null)
  const [storeModalOpen, setStoreModalOpen] = useState(false)
  const [activeStoreCode, setActiveStoreCode] = useState<string>('')
  const [storeOrders, setStoreOrders] = useState<Detail[]>([])
  const [storePagination, setStorePagination] = useState({ current: 1, pageSize: 10, total: 0 })

  const [filters, setFilters] = useState({
    status: undefined as string | undefined,
    orderId: '',
    storeCode: undefined as string | undefined,
    pickUpDateRange: undefined as [string, string] | undefined,
  })

  const fetchOrders = useCallback(async (page?: number, pageSize?: number) => {
    setLoading(true)
    try {
      const current = page ?? pagination.current
      const limit = pageSize ?? pagination.pageSize

      const params = new URLSearchParams()
      params.append('page', String(current))
      params.append('limit', String(limit))

      if (filters.orderId) params.append('order_id', filters.orderId)
      if (filters.status) params.append('status', filters.status)
      if (filters.storeCode) params.append('store_code', filters.storeCode)
      if (filters.pickUpDateRange?.[0]) params.append('pick_up_start', filters.pickUpDateRange[0])
      if (filters.pickUpDateRange?.[1]) params.append('pick_up_end', filters.pickUpDateRange[1])

      const response = await apiService.get<APIResponse<Detail[]>>(`/hktv-orders?${params.toString()}`)
      if (!response?.success) throw new Error('Fetch failed')

      const data = Array.isArray(response.data) ? response.data : []
      setOrders(data)

      const p = response.pagination || { page: current, limit, total: data.length, totalPages: 1 }
      setPagination({ current: p.page || current, pageSize: p.limit || limit, total: p.total || data.length })
    } catch (error) {
      console.error('Error fetching HKTVmall orders:', error)
      message.error('載入 HKTVmall 訂單列表失敗，請稍後重試')
    } finally {
      setLoading(false)
    }
  }, [filters, pagination.current, pagination.pageSize])

  useEffect(() => {
    fetchOrders(1, pagination.pageSize)
  }, [filters])

  const extractStoreCode = useCallback((orderId?: string) => {
    if (!orderId) return 'UNKNOWN'
    const id = String(orderId).trim()
    const match = id.match(/-([A-Za-z]\d{5,})$/)
    if (match && match[1]) return match[1].toUpperCase()
    if (id.includes('-')) return id.split('-').pop()!.toUpperCase()
    return 'UNKNOWN'
  }, [])

  const filteredOrders = useMemo(() => orders, [orders])

  const [storeSummary, setStoreSummary] = useState<StoreGroup[]>([])

  const fetchStoreSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (filters.orderId) params.append('order_id', filters.orderId)
      params.append('status', filters.status || 'CONFIRMED')
      if (filters.storeCode) params.append('store_code', filters.storeCode)
      if (filters.pickUpDateRange?.[0]) params.append('pick_up_start', filters.pickUpDateRange[0])
      if (filters.pickUpDateRange?.[1]) params.append('pick_up_end', filters.pickUpDateRange[1])
      const resp = await apiService.get<APIResponse<{ storeCode: string; totalOrders: number }[]>>(`/hktv-orders/summary?${params.toString()}`)
      if (!resp?.success) throw new Error('Load summary failed')
      const rows = Array.isArray(resp.data) ? resp.data : []
      const mapped: StoreGroup[] = rows.map(r => ({ storeCode: r.storeCode, totalOrders: r.totalOrders, orders: [] }))
      setStoreSummary(mapped)
    } catch (e) {
      console.error('Load store summary failed:', e)
      setStoreSummary([])
    }
  }, [filters])

  useEffect(() => { fetchStoreSummary() }, [fetchStoreSummary])

  const storeColumns = useMemo<TableColumnsType<Detail>>(() => [
    {
      title: '托運單號 / Tracking',
      dataIndex: 'consignmentCode',
      key: 'consignmentCode',
      width: 220,
      ellipsis: true,
      render: (_: string, record: Detail) => (
        <Space direction="vertical" size={0}>
          <span>{record.consignmentCode || '-'}</span>
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>Tracking: {record.trackingId || '-'}</span>
        </Space>
      ),
    },
    {
      title: '訂單號',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 220,
      ellipsis: true,
    },
    {
      title: '狀態',
      dataIndex: 'consignmentStatus',
      key: 'status',
      width: 160,
      render: (status?: string) => {
        const normalized = (status || 'UNKNOWN').toUpperCase()
        return <Tag color={getStatusColor(normalized)}>{normalized}</Tag>
      },
    },
    {
      title: '下單時間',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 220
    },
    {
      title: '配送方式',
      dataIndex: 'deliveryMethod',
      key: 'deliveryMethod',
      ellipsis: true,
    },
  ], [])

  const storeCodeOptions = useMemo(() => {
    // 以當前頁面的彙總卡片（storeSummary）來決定下拉可選的 store code
    const codesFromSummary = storeSummary.map(g => String(g.storeCode || '').trim()).filter(Boolean)
    const uniqFromSummary = Array.from(new Set(codesFromSummary))
    if (uniqFromSummary.length > 0) {
      return uniqFromSummary.sort((a, b) => a.localeCompare(b))
    }
    // 回退：若彙總未有資料，從當前訂單列表自行萃取
    const set = new Set<string>()
    orders.forEach(order => {
      const sc = extractStoreCode(order.orderId)
      if (sc && sc !== 'UNKNOWN') set.add(sc)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [storeSummary, orders, extractStoreCode])

  const statusOptions = useMemo<SelectProps<string>['options']>(() => {
    const map = new Map<string, string>()
    BASE_STATUS_SEQUENCE.forEach(status => map.set(status, status))
    orders.forEach(order => {
      const raw = (order.consignmentStatus || '').trim()
      if (!raw) return
      const upper = raw.toUpperCase()
      if (!map.has(upper)) map.set(upper, upper)
    })
    return Array.from(map.values()).map(value => ({ label: value, value }))
  }, [orders])

  const handleRefresh = async () => {
    try {
      await Promise.all([
        fetchOrders(1, pagination.pageSize),
        fetchStoreSummary(),
        storeModalOpen && activeStoreCode
          ? fetchStoreOrders(undefined, storePagination.current, storePagination.pageSize)
          : Promise.resolve(),
      ])
      message.success('已刷新')
    } catch (e) {
      // no-op; 具體錯誤已在各自函數內處理
    }
  }

  const fetchStoreOrders = useCallback(async (storeCode?: string, page?: number, pageSize?: number) => {
    const code = storeCode ?? activeStoreCode
    if (!code) return
    setLoading(true)
    try {
      const current = page ?? storePagination.current
      const limit = pageSize ?? storePagination.pageSize

      const params = new URLSearchParams()
      params.append('page', String(current))
      params.append('limit', String(limit))
      params.append('store_code', code)
      params.append('status', filters.status || 'CONFIRMED')

      if (filters.orderId) params.append('order_id', filters.orderId)
      if (filters.pickUpDateRange?.[0]) params.append('pick_up_start', filters.pickUpDateRange[0])
      if (filters.pickUpDateRange?.[1]) params.append('pick_up_end', filters.pickUpDateRange[1])

      const response = await apiService.get<APIResponse<Detail[]>>(`/hktv-orders?${params.toString()}`)
      if (!response?.success) throw new Error('Fetch failed')

      const data = Array.isArray(response.data) ? response.data : []
      const statusExpected = (filters.status || 'CONFIRMED').toUpperCase()
      const statusFiltered = data.filter(order => (order.consignmentStatus || '').toUpperCase() === statusExpected)
      setStoreOrders(statusFiltered)

      const p = response.pagination || { page: current, limit: data.length, total: data.length, totalPages: 1 }
      setStorePagination({ current: p.page || current, pageSize: p.limit || limit, total: p.total || statusFiltered.length })
    } catch (error) {
      console.error('Error fetching store orders:', error)
      message.error('載入該店鋪訂單失敗，請稍後重試')
    } finally {
      setLoading(false)
    }
  }, [activeStoreCode, filters, storePagination.current, storePagination.pageSize])

  const handleExportExcel = () => {
    try {
      if (!filteredOrders || filteredOrders.length === 0) {
        message.warning('沒有數據可導出')
        return
      }

      const headers = [
        '運單號',
        '訂單號',
        '跟蹤ID',
        '交易ID',
        '訂單日期',
        '狀態',
        '取貨日期',
        '配送日期',
        '配送方式',
        '收件人',
        '聯繫電話',
        '配送地址',
        '倉庫ID',
        '配送倉庫',
        '留言',
        '商品數量',
        '創建時間',
        '更新時間',
      ]

      const rows = filteredOrders.map(order => [
        order.consignmentCode || '',
        order.orderId || '',
        order.trackingId || '',
        order.transactionId || '',
        order.orderDate ? `\u200B${new Date(order.orderDate).toISOString().split('T')[0]}` : '',
        (order.consignmentStatus || '').toUpperCase(),
        order.pickUpDate ? `\u200B${new Date(order.pickUpDate).toISOString().split('T')[0]}` : '',
        order.deliveryDate ? `\u200B${new Date(order.deliveryDate).toISOString().split('T')[0]}` : '',
        order.deliveryMethod || '',
        order.recipientName || '',
        order.contactPhoneNumber || '',
        order.deliveryAddress || '',
        order.warehouseId || '',
        order.deliverToWarehouse || '',
        order.message || '',
        order.consignmentEntries?.length || 0,
        order.createdAt ? `\u200B${new Date(order.createdAt).toISOString().split('T')[0]}` : '',
        order.updatedAt ? `\u200B${new Date(order.updatedAt).toISOString().split('T')[0]}` : '',
      ])

      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n')
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `hktv_orders_export_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      message.success('訂單數據已成功導出')
    } catch (error) {
      console.error('導出失敗:', error)
      message.error('導出失敗，請稍後重試')
    }
  }

  const handleOpenStore = (group: StoreGroup) => {
    setActiveStoreCode(group.storeCode)
    setStoreModalOpen(true)
    setStorePagination(prev => ({ ...prev, current: 1 }))
    setTimeout(() => fetchStoreOrders(group.storeCode, 1, storePagination.pageSize), 0)
  }

  const handleCloseStore = () => {
    setStoreModalOpen(false)
    setStoreOrders([])
    setActiveStoreCode('')
  }

  const storeTablePagination = useMemo(() => {
    return {
      current: storePagination.current,
      pageSize: storePagination.pageSize,
      total: storePagination.total,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50', '100'],
      onChange: (page: number, pageSize?: number) => {
        const size = pageSize || storePagination.pageSize
        setStorePagination({ current: page, pageSize: size, total: storePagination.total })
        fetchStoreOrders(undefined, page, size)
      }
    }
  }, [storePagination, fetchStoreOrders])

  const pickUpRangeValue = useMemo(() => {
    if (!filters.pickUpDateRange) return undefined
    return [moment(filters.pickUpDateRange[0]), moment(filters.pickUpDateRange[1])]
  }, [filters.pickUpDateRange])

  const items = (selected?.consignmentEntries || []).map(entry => ({
    key: entry.skuId,
    skuId: entry.skuId,
    name: entry.skuNameZh || entry.skuNameEn,
    brand: entry.brand,
    qty: entry.quantity,
    total: entry.totalPrice,
  }))

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        extra={
          <Space wrap>
            {/*<Input
              value={filters.orderId}
              onChange={e => setFilters(prev => ({ ...prev, orderId: e.target.value }))}
              placeholder="訂單號"
              allowClear
              style={{ width: 260 }}
            />*/}
            <Select
              showSearch
              allowClear
              placeholder="Store Code"
              value={filters.storeCode}
              onChange={value => setFilters(prev => ({ ...prev, storeCode: value }))}
              style={{ width: 180 }}
              options={storeCodeOptions.map(code => ({ label: code, value: code }))}
              filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
            />
            {/*<Select
              value={filters.status}
              onChange={value => setFilters(prev => ({ ...prev, status: value || undefined }))}
              placeholder="狀態"
              style={{ width: 180 }}
              allowClear
              options={statusOptions}
              showSearch
              optionFilterProp="label"
            />*/}
            <DatePicker.RangePicker
              value={pickUpRangeValue as any}
              onChange={(dates) => {
                const start = dates?.[0]
                const end = dates?.[1]
                if (start && end) {
                  setFilters(prev => ({
                    ...prev,
                    pickUpDateRange: [start.startOf('day').toISOString(), end.endOf('day').toISOString()],
                  }))
                } else {
                  setFilters(prev => ({ ...prev, pickUpDateRange: undefined }))
                }
              }}
              allowClear
              placeholder={["開始日期", "結束日期"]}
              style={{ width: 260 }}
            />
            <Button icon={<ReloadOutlined />} size="small" onClick={handleRefresh} loading={loading}>
              刷新
            </Button>
            <Button icon={<FileExcelOutlined />} size="small" onClick={handleExportExcel} disabled={!filteredOrders.length}>
              導出
            </Button>
          </Space>
        }
      >
        <Spin spinning={loading} tip="載入中...">
          {storeSummary.length === 0 ? (
            <Empty description="暫無訂單" style={{ padding: '40px 0' }} />
          ) : (
            <>
              <Space style={{ marginBottom: 16 }}>
                <Statistic title="彙總訂單總數" value={storeSummary.reduce((sum, r) => sum + r.totalOrders, 0)} />
                <Statistic title="店鋪數量" value={storeSummary.length} />
              </Space>
              <Row gutter={[16, 16]}>
                {storeSummary.map(group => (
                  <Col key={group.storeCode} xs={24} sm={12} md={8} lg={6} xl={6}>
                    <Card
                      hoverable
                      onClick={() => handleOpenStore(group)}
                      style={{
                        cursor: 'pointer',
                        minHeight: 150,
                        borderRadius: 16,
                        border: '1px solid #d9d9d9',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                      }}
                      bodyStyle={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
                    >
                      <Space direction="vertical" align="center" size={8} style={{ width: '100%' }}>
                        <Tag color="geekblue" style={{ fontSize: 16, padding: '4px 16px' }}>
                          {group.storeCode}
                        </Tag>
                        <Statistic title="總訂單數" value={group.totalOrders} valueStyle={{ fontSize: 26 }} />
                        <span style={{ color: '#999' }}>點擊查看詳細訂單</span>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Spin>
      </Card>

      <Modal
        title={`Store ${activeStoreCode} 訂單列表`}
        open={storeModalOpen}
        onCancel={handleCloseStore}
        footer={null}
        width={1100}
        destroyOnClose
      >
        <Table
          rowKey={record =>
            record.id?.toString() || record.orderId || record.consignmentCode || record.transactionId || Math.random().toString(36).slice(2)
          }
          dataSource={storeOrders}
          columns={storeColumns}
          pagination={storeTablePagination as any}
          onRow={record => ({
            onClick: () => setSelected(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Modal>

      <Drawer
        title={`詳情 - ${fmt(selected?.orderId)}`}
        placement="right"
        width={720}
        open={!!selected}
        onClose={() => setSelected(null)}
        destroyOnClose
      >
        {selected && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title="基本資訊">
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Consignment Code">{fmt(selected.consignmentCode)}</Descriptions.Item>
                <Descriptions.Item label="Tracking ID">{fmt(selected.trackingId)}</Descriptions.Item>
                <Descriptions.Item label="Transaction ID" span={2}>
                  {fmt(selected.transactionId)}
                </Descriptions.Item>
                <Descriptions.Item label="Order Date">{fmt(selected.orderDate)}</Descriptions.Item>
                <Descriptions.Item label="Status">{fmt(selected.consignmentStatus)}</Descriptions.Item>
                <Descriptions.Item label="Pick Up Date">{fmt(selected.pickUpDate)}</Descriptions.Item>
                <Descriptions.Item label="Delivery Date">{fmt(selected.deliveryDate)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="配送資訊">
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Delivery Method">{fmt(selected.deliveryMethod)}</Descriptions.Item>
                <Descriptions.Item label="Warehouse ID">{fmt(selected.warehouseId)}</Descriptions.Item>
                <Descriptions.Item label="Deliver To Warehouse" span={2}>
                  {fmt(selected.deliverToWarehouse)}
                </Descriptions.Item>
                <Descriptions.Item label="Recipient Name">{fmt(selected.recipientName)}</Descriptions.Item>
                <Descriptions.Item label="Phone">{fmt(selected.contactPhoneNumber)}</Descriptions.Item>
                <Descriptions.Item label="Address" span={2}>
                  {fmt(selected.deliveryAddress)}
                </Descriptions.Item>
                <Descriptions.Item label="Message" span={2}>
                  {fmt(selected.message)}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="商品明細">
              <Table
                rowKey="key"
                dataSource={items}
                pagination={false}
                columns={[
                  { title: 'SKU', dataIndex: 'skuId', width: 220 },
                  { title: '商品名稱', dataIndex: 'name' },
                  { title: '品牌', dataIndex: 'brand', width: 120 },
                  { title: '數量', dataIndex: 'qty', width: 100 },
                  { title: '金額', dataIndex: 'total', width: 120 },
                ]}
              />
            </Card>
          </Space>
        )}
      </Drawer>
    </Space>
  )
}

export default HKTVmallOrder